import * as React from 'react'
import { useT } from '@/components/visitone/visitone-provider'

export type ErrorSummaryItem = { key: string; label: string; message: string }

/*
 * ErrorSummary (VIS-321): shown at the top of a form after a save attempt with
 * errors, and focused then. Each item names its field and moves to it; the
 * form decides how (expand the section, switch content language, focus).
 * Messages that belong to no field are listed as text. `title` replaces the
 * default "…before you can save" heading when the form's action is not a
 * save, such as sending; it is already translated and counts the problems.
 */
export function ErrorSummary({
  errors,
  messages = [],
  onJump,
  title,
  ref,
}: {
  errors: ErrorSummaryItem[]
  messages?: string[]
  onJump: (key: string) => void
  title?: string
  ref?: React.Ref<HTMLDivElement>
}) {
  const t = useT()
  const titleId = React.useId()
  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="alert"
      aria-labelledby={titleId}
      className="mb-4 rounded-md border border-danger/60 bg-danger-surface px-4 py-3 outline-none"
    >
      <h2 id={titleId} className="text-sm font-semibold text-danger">
        {title ?? t('forms.errorSummary.title', { count: errors.length + messages.length })}
      </h2>
      <ul className="mt-1.5 flex flex-col gap-1 text-sm">
        {errors.map((error) => (
          <li key={error.key}>
            <button
              type="button"
              className="min-h-6 text-left text-danger underline underline-offset-4"
              onClick={() => onJump(error.key)}
            >
              {t('forms.errorSummary.item', { label: error.label, message: error.message })}
            </button>
          </li>
        ))}
        {messages.map((message) => (
          <li key={message} className="text-danger">
            {message}
          </li>
        ))}
      </ul>
    </div>
  )
}
